/**
 * Phạm vi cổng — nền tảng hay tenant.
 *
 * Một bản dựng phục vụ **hai nhóm người dùng tách biệt**:
 *
 * - **Nền tảng** (`nen-tang`): nhân sự FoxAI vận hành chính nền tảng. Họ chỉ
 *   thấy nhóm `QUẢN TRỊ NỀN TẢNG` sinh từ `app/modules.ts` — tenant, gói dịch
 *   vụ, hàng đợi việc, nhật ký, người dùng, vai trò, khoá API.
 * - **Tenant** (`tenant`): người dùng của một tổ chức khách hàng. Họ thấy mọi
 *   nhóm nghiệp vụ ở `components/layout/nav-config.ts` và **không bao giờ**
 *   thấy nhóm quản trị nền tảng.
 *
 * Hai tập này **loại trừ nhau**, không lồng nhau: một người quản trị nền tảng
 * không phải là "tenant admin có thêm quyền". Họ không thuộc tenant nào nên
 * những màn như `Quản lý tri thức` hay `Xử lý chứng từ` không có dữ liệu để
 * hiển thị — bày ra chỉ dẫn tới màn rỗng khó hiểu.
 *
 * ⚠️ Phạm vi **không thay** kiểm quyền. Nó trả lời "màn này có thuộc cổng của
 * người này không", còn `quyen` trong registry trả lời "người này có được vào
 * màn đó không". Phải qua cả hai cửa; xem `duocVao` ở `app/modules.ts`.
 */

/** Hai cổng của cùng một bản dựng. */
export type PhamViCong = 'nen-tang' | 'tenant';

/**
 * Vai trò lớp 1 (`realm_access.roles`, DT21 Chương 7) của **nhân sự nền tảng**.
 *
 * Danh sách này là luật phân loại duy nhất: có bất kỳ vai trò nào ở đây thì
 * người dùng thuộc cổng nền tảng, ngược lại thuộc cổng tenant. Đặt theo danh
 * sách trắng chứ không theo danh sách đen vì vai trò lớp 2 của tenant do từng
 * tenant tự đặt (ADR-07) — không thể liệt kê trước, và nếu liệt kê thiếu thì
 * một vai trò lạ sẽ lọt vào cổng nền tảng.
 */
const VAI_TRO_NEN_TANG: readonly string[] = [
  'PLATFORM_ADMIN',
  'AUDITOR',
  'VENDOR',
  // ADR-0058: người cấp, người duyệt, người xem license là nhân sự FoxAI.
  'LICENSE_ISSUER',
  'LICENSE_APPROVER',
  'LICENSE_VIEWER',
];

/**
 * Khoá `roles` nội bộ mà `lib/auth/oidc.ts` gắn cho nhân sự nền tảng.
 *
 * `oidc.ts` dịch role lớp 1 của Keycloak sang khoá nhãn (`vaiTro.*`) cộng với
 * `SUPER_ADMIN`, nên đến lúc kho trạng thái giữ hồ sơ thì tên role gốc đã mất.
 * Nhận cả hai dạng để luật chạy đúng dù hồ sơ tới từ Keycloak hay từ dữ liệu
 * mẫu.
 */
const KHOA_NOI_BO_NEN_TANG: readonly string[] = [
  'SUPER_ADMIN',
  'vaiTro.quanTriNenTang',
  'vaiTro.kiemToan',
  'vaiTro.doiTacPhatTrienModule',
  'vaiTro.phuTrachLicense',
];

/**
 * Phạm vi của một người dùng, suy từ vai trò lớp 1.
 *
 * Chưa đăng nhập thì trả `tenant`: đó là mặc định hẹp hơn, và `AuthGate` sẽ
 * đẩy về màn đăng nhập trước khi có gì được vẽ ra.
 */
export function phamViCuaVaiTro(roles: readonly string[] | undefined): PhamViCong {
  if (!roles || roles.length === 0) return 'tenant';
  const laNenTang = roles.some(
    (r) => VAI_TRO_NEN_TANG.includes(r) || KHOA_NOI_BO_NEN_TANG.includes(r),
  );
  return laNenTang ? 'nen-tang' : 'tenant';
}
